import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, Flex, Icon } from '@kognitos/lattice';

import type { TRainValueJs } from '../utils/value';

/**
 * How many entries of a container render before a "Show more" row takes over.
 * A tool result can carry thousands of rows, and rendering them all puts that
 * cost on the transcript rather than on the one payload the user opened.
 */
const PAGE_SIZE = 10;

/**
 * Containers larger than this open collapsed, so expanding a card with one big
 * argument does not bury the rest of the payload. Small ones open expanded —
 * a two-item list costs nothing to show and hiding it just adds a click.
 */
const COLLAPSE_ENTRIES_OVER = 5;

/** Beyond this a string is clamped, with the full text one click away. */
const LONG_STRING_CHARS = 240;

/** Indent per nesting level, in px. Aligns a child under its parent's label. */
const INDENT_PX = 12;

/** Width of the chevron column, which scalar rows pad by to stay aligned. */
const CHEVRON_COL = 'w-3.5';

/**
 * The kinds the viewer distinguishes. Deliberately coarser than Rain's `Value`
 * oneof: `rainv1ValueToJs` already flattens artifacts to a display string and
 * drops the discriminant, so by the time a value reaches the UI a URI is
 * indistinguishable from any other string.
 *
 * Kind is never LABELLED — it is carried by how the value is DRAWN (quotes on
 * strings, a distinct tone on literals), the way a code editor does it. A badge
 * column spent horizontal space, in a pane that can be 40% of the split,
 * restating what `"5"` versus `5` already shows.
 */
type TValueKind =
  | 'str'
  | 'num'
  | 'bool'
  | 'null'
  | 'date'
  | 'time'
  | 'datetime'
  | 'duration'
  | 'list'
  | 'object';

type TPlainObject = { [key: string]: TRainValueJs };

const isPlainObject = (value: TRainValueJs): value is TPlainObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Rain's date/time/duration values decode to their raw envelopes rather than to
 * strings (see `rainv1ValueToJs`), so the viewer has to recognise them by shape
 * — otherwise a due date renders as `{ year: 2026, month: 9, day: 3 }`, which is
 * the object dump this component exists to replace.
 *
 * Matched on an exact key set rather than on the presence of one field: a
 * business payload may well carry its own `{ year, month }` object, and calling
 * that a date would format away fields it does not know about.
 */
const hasExactKeys = (value: TPlainObject, keys: string[]): boolean => {
  const present = Object.keys(value);
  /*
   * EVERY key, not a subset. A subset match let `{ year: 2026 }` pass as a date,
   * which `formatScalar` then padded into the fabricated `2026-00-00` — inventing
   * a month and a day the payload never carried. Requiring the full envelope can
   * only ever fall back to rendering the real object, which is always honest.
   */
  return present.length === keys.length && keys.every(key => key in value);
};

const kindOf = (value: TRainValueJs): TValueKind => {
  if (value === null) return 'null';
  if (typeof value === 'string') return 'str';
  if (typeof value === 'number') return 'num';
  if (typeof value === 'boolean') return 'bool';
  if (Array.isArray(value)) return 'list';
  if (isPlainObject(value)) {
    if (hasExactKeys(value, ['year', 'month', 'day'])) return 'date';
    if (hasExactKeys(value, ['hour', 'minute', 'second'])) return 'time';
    if (hasExactKeys(value, ['rfc3339'])) return 'datetime';
    if (hasExactKeys(value, ['seconds', 'nanos'])) return 'duration';
    return 'object';
  }
  return 'object';
};

const pad = (value: number | undefined, width = 2): string =>
  String(value ?? 0).padStart(width, '0');

/**
 * A scalar as the single line the row shows. Containers never reach here —
 * they render a summary and their children instead.
 */
const formatScalar = (value: TRainValueJs, kind: TValueKind): string => {
  switch (kind) {
    case 'null':
      return 'null';
    case 'num':
    case 'bool':
      return String(value);
    case 'date': {
      const { year, month, day } = value as {
        year?: number;
        month?: number;
        day?: number;
      };
      return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
    }
    case 'time': {
      const { hour, minute, second } = value as {
        hour?: number;
        minute?: number;
        second?: number;
      };
      return `${pad(hour)}:${pad(minute)}:${pad(second)}`;
    }
    case 'datetime':
      return (value as { rfc3339?: string }).rfc3339 ?? '';
    case 'duration': {
      const { seconds, nanos } = value as {
        seconds?: string;
        nanos?: number;
      };
      const total = Number(seconds ?? 0) + (nanos ?? 0) / 1e9;
      return `${total}s`;
    }
    default:
      return String(value);
  }
};

/**
 * The tone a scalar is drawn in — the badge column's job, done by the value
 * itself. Strings keep the plain foreground, so the text you actually read is
 * the most legible thing here; every other scalar sits back from it.
 */
const scalarToneClass = (kind: TValueKind): string => {
  /*
   * Literals read one step BACK from prose, not forward. The first pass gave
   * them `text-primary`, which in this theme is a bright yellow-green — a bare
   * `false` then pulled more attention than any string in the payload, which is
   * backwards. They only need to be distinguishable from a quoted `"false"`,
   * and the missing quotes already do most of that work.
   */
  if (
    kind === 'num' ||
    kind === 'bool' ||
    kind === 'null' ||
    kind === 'date' ||
    kind === 'time' ||
    kind === 'datetime' ||
    kind === 'duration'
  ) {
    return 'text-muted-foreground';
  }
  return 'text-foreground';
};

/** Entries of a container, as the label/value pairs the rows render. */
const entriesOf = (
  value: TRainValueJs,
  kind: TValueKind,
): Array<{ key: string; value: TRainValueJs }> => {
  if (kind === 'list') {
    return (value as TRainValueJs[]).map((item, index) => ({
      key: String(index),
      value: item,
    }));
  }
  if (kind === 'object') {
    return Object.entries(value as TPlainObject).map(([key, item]) => ({
      key,
      value: item,
    }));
  }
  return [];
};

/** A row's key. Muted and mono, so the value beside it stays the focus. */
function ValueKey({ label }: { label: string }) {
  return (
    <span
      className="shrink-0 font-mono text-xs text-muted-foreground"
      data-testid="rain-value-key"
    >
      {label}
    </span>
  );
}

/**
 * A string long enough to bury the rest of the payload, clamped with an inline
 * expander. Multi-line strings keep their newlines in a `<pre>`: a tool that
 * returned a file or a traceback is unreadable as an escaped one-liner.
 */
function StringValue({ value }: { value: string }) {
  const { t } = useTranslation('rain');
  const [expanded, setExpanded] = useState(false);

  const isMultiline = value.includes('\n');
  const isLong = value.length > LONG_STRING_CHARS;

  /*
   * Quoted, and that is now load-bearing: with the kind badge gone, the quotes
   * are what separate the string `"5"` from the number `5`, and what makes an
   * empty string read as `""` rather than as a missing value.
   */
  if (!isMultiline && !isLong) {
    return (
      <span className="min-w-0 break-all font-mono text-xs text-foreground">
        {JSON.stringify(value)}
      </span>
    );
  }

  const shown = expanded ? value : value.slice(0, LONG_STRING_CHARS);

  return (
    <span className="min-w-0 flex-1">
      {/* Long prose reads unquoted: at this length it is obviously text, and
          wrapping a paragraph in quotes just adds noise at both ends. */}
      <pre className="min-w-0 whitespace-pre-wrap break-words font-mono text-xs text-foreground">
        {shown}
        {!expanded && isLong ? '…' : ''}
      </pre>
      <button
        type="button"
        onClick={() => setExpanded(current => !current)}
        className="mt-0.5 text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      >
        {expanded
          ? t('rain.valueViewer.showLess')
          : t('rain.valueViewer.showMore', {
              count: value.length - LONG_STRING_CHARS,
            })}
      </button>
    </span>
  );
}

interface IValueRowProps {
  /** Key or index this value sits under. Absent for the root. */
  label?: string;
  value: TRainValueJs;
  depth: number;
}

/**
 * One value, as a row.
 *
 * A scalar is a single line (`label  value`) with no disclosure — a flat
 * argument object then reads as a table rather than a tree the user has to
 * unfold key by key. Only containers get a chevron, which is what makes the
 * common case (a handful of string arguments) legible at a glance.
 */
function ValueRow({ label, value, depth }: IValueRowProps) {
  const { t } = useTranslation('rain');
  const kind = kindOf(value);
  const isContainer = kind === 'list' || kind === 'object';

  const entries = useMemo(
    () => (isContainer ? entriesOf(value, kind) : []),
    [isContainer, kind, value],
  );

  const [expanded, setExpanded] = useState(
    entries.length > 0 && entries.length <= COLLAPSE_ENTRIES_OVER,
  );
  const [shownCount, setShownCount] = useState(PAGE_SIZE);

  const indent = { paddingLeft: `${depth * INDENT_PX}px` };
  const rowClass = 'min-w-0 py-[3px] leading-5';

  if (!isContainer) {
    return (
      <Flex
        align="start"
        gap={2}
        className={rowClass}
        style={indent}
        data-testid="rain-value-row"
      >
        {/* Holds the chevron's width so scalar rows align with container ones
            instead of hanging half a glyph to the left of them. */}
        <span className={cn(CHEVRON_COL, 'shrink-0')} aria-hidden />
        {label !== undefined && <ValueKey label={label} />}
        {kind === 'str' ? (
          <StringValue value={value as string} />
        ) : (
          <span
            className={cn(
              'min-w-0 break-all font-mono text-xs',
              scalarToneClass(kind),
            )}
          >
            {formatScalar(value, kind)}
          </span>
        )}
      </Flex>
    );
  }

  const summary =
    kind === 'list'
      ? t('rain.valueViewer.items', { count: entries.length })
      : t('rain.valueViewer.fields', { count: entries.length });

  const visible = entries.slice(0, shownCount);
  const remaining = entries.length - visible.length;

  return (
    <>
      <Flex
        align="center"
        gap={2}
        className={rowClass}
        style={indent}
        data-testid="rain-value-row"
      >
        {/* An empty container has nothing to disclose, so it renders its
            summary without a control that would do nothing. */}
        {entries.length > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded(current => !current)}
            aria-expanded={expanded}
            className="group flex min-w-0 items-center gap-2 text-left"
            data-testid="rain-value-toggle"
          >
            <Icon
              type={expanded ? 'ChevronDown' : 'ChevronRight'}
              size="sm"
              className={cn(
                CHEVRON_COL,
                'shrink-0 text-muted-foreground/60 transition-colors group-hover:text-foreground',
              )}
            />
            {label !== undefined && <ValueKey label={label} />}
            <span className="shrink-0 text-xs text-muted-foreground/70">
              {summary}
            </span>
          </button>
        ) : (
          <>
            <span className={cn(CHEVRON_COL, 'shrink-0')} aria-hidden />
            {label !== undefined && <ValueKey label={label} />}
            <span className="shrink-0 text-xs text-muted-foreground/70">
              {summary}
            </span>
          </>
        )}
      </Flex>

      {expanded &&
        visible.map(entry => (
          <ValueRow
            key={entry.key}
            label={entry.key}
            value={entry.value}
            depth={depth + 1}
          />
        ))}

      {expanded && remaining > 0 && (
        // Indented to the children's text column, not their chevron, so the
        // control reads as belonging to the list rather than as another entry.
        <div style={{ paddingLeft: `${(depth + 1) * INDENT_PX + 22}px` }}>
          <button
            type="button"
            className="py-0.5 text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            onClick={() => setShownCount(current => current + PAGE_SIZE)}
            data-testid="rain-value-load-more"
          >
            {t('rain.valueViewer.loadMore', { count: remaining })}
          </button>
        </div>
      )}
    </>
  );
}

export interface IRainValueViewerProps {
  value: TRainValueJs;
  /**
   * Renders a top-level container's entries directly, without a row for the
   * container itself.
   *
   * The arguments object is the case: it is always an object, the section is
   * already labelled "Arguments", so a row reading `3 fields` above the
   * arguments is a line that says nothing and a click that hides them.
   */
  flattenRoot?: boolean;
  className?: string;
}

/**
 * A decoded Rain value, as an indented tree of rows.
 *
 * Replaces `JSON.stringify(value, null, 2)` in a `<pre>`, which is what the
 * tool-call card used to show: readable for three scalar arguments and useless
 * for anything a real tool returns. Structure and the ability to collapse a
 * branch are what make a payload something you can actually read.
 *
 * Horizontal overflow scrolls HERE rather than being wrapped, so a single wide
 * value cannot stretch the transcript that contains it.
 */
export function RainValueViewer({
  value,
  flattenRoot = false,
  className,
}: IRainValueViewerProps) {
  const rootKind = kindOf(value);
  const rootEntries =
    flattenRoot && (rootKind === 'list' || rootKind === 'object')
      ? entriesOf(value, rootKind)
      : undefined;

  return (
    <div
      className={cn('min-w-0 max-w-full overflow-x-auto', className)}
      data-testid="rain-value-viewer"
    >
      {rootEntries ? (
        rootEntries.map(entry => (
          <ValueRow
            key={entry.key}
            label={entry.key}
            value={entry.value}
            depth={0}
          />
        ))
      ) : (
        <ValueRow value={value} depth={0} />
      )}
    </div>
  );
}
